import React from 'react'

import { LocaleLink } from '@/components/LocaleLink'
import { cn } from '@/utilities/ui'

import type { MarcaResolvida } from './modeloMarca'

type Props = {
  marca: MarcaResolvida
  /** Fundo em que o logo está (escolhe a variante clara/escura). */
  tone: 'claro' | 'escuro'
  /** Header compacto: esconde o wordmark com clip-path. */
  compacto?: boolean
  /** Alturas em px (desktop) — padrão vem de `marca`. */
  alturaSimbolo?: number
  alturaWordmark?: number
  /** Altura do símbolo no móvel (o wordmark escala na mesma proporção). */
  alturaMovel?: number
  href?: string | null
  className?: string
  mostrarWordmark?: boolean
}

/**
 * As duas variantes (fundo claro e fundo escuro) ficam sempre no DOM, empilhadas, e só a
 * opacidade alterna. Trocar o `src` fazia o logo sumir ao abrir o mega menu (header branco)
 * enquanto a outra versão ainda carregava.
 */
function Variantes({ claro, escuro, tone, alt }: { claro: string; escuro: string; tone: 'claro' | 'escuro'; alt: string }) {
  if (claro === escuro) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={claro} alt={alt} className="block h-full w-auto max-w-none" draggable={false} />
  }
  const fade = 'col-start-1 row-start-1 block h-full w-auto max-w-none transition-opacity duration-200 motion-reduce:transition-none'
  return (
    <span className="grid h-full">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={escuro} alt={tone === 'escuro' ? alt : ''} aria-hidden={tone !== 'escuro'} className={cn(fade, tone === 'escuro' ? 'opacity-100' : 'opacity-0')} draggable={false} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={claro} alt={tone === 'claro' ? alt : ''} aria-hidden={tone !== 'claro'} className={cn(fade, tone === 'claro' ? 'opacity-100' : 'opacity-0')} draggable={false} />
    </span>
  )
}

function Par({
  marca,
  tone,
  fechado,
  hS,
  hW,
  mostrarWordmark,
  className,
}: {
  marca: MarcaResolvida
  tone: 'claro' | 'escuro'
  fechado: boolean
  hS: number
  hW: number
  mostrarWordmark: boolean
  className: string
}) {
  return (
    <span className={cn('shrink-0 items-center gap-[6px] whitespace-nowrap lg:gap-2', className)}>
      <span
        className="flex items-center transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-[1.04] motion-reduce:transition-none"
        style={{ height: hS }}
      >
        <Variantes claro={marca.simbolo} escuro={marca.simboloEscuro} tone={tone} alt={marca.nome} />
      </span>
      {mostrarWordmark ? (
        <span
          aria-hidden
          className="flex items-center overflow-hidden transition-[clip-path,width,opacity] duration-[350ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] motion-reduce:transition-none"
          style={{
            height: hW,
            clipPath: fechado ? 'inset(0 100% 0 0)' : 'inset(0 0% 0 0)',
            width: fechado ? 0 : 'auto',
            opacity: fechado ? 0 : 1,
          }}
        >
          <Variantes claro={marca.wordmark} escuro={marca.wordmarkEscuro} tone={tone} alt="" />
        </span>
      ) : null}
    </span>
  )
}

/**
 * Símbolo + wordmark em linha. No header compacto o wordmark fecha com `clip-path: inset(0 100% 0 0)`
 * e largura 0 (transição 350 ms); o símbolo tem leve `scale` no hover do grupo.
 * Duas versões (móvel/desktop) com alturas diferentes; só uma é exibida por breakpoint.
 */
export function LogoMarca({
  marca,
  tone,
  compacto = false,
  alturaSimbolo,
  alturaWordmark,
  alturaMovel,
  href = '/',
  className,
  mostrarWordmark = true,
}: Props) {
  const hS = alturaSimbolo ?? marca.alturaSimboloPx
  const hW = alturaWordmark ?? marca.alturaWordmarkPx
  const hM = alturaMovel ?? marca.alturaMovelPx
  const hMW = Math.round((hM * hW) / hS)
  const fechado = compacto && marca.esconderWordmarkAoRolar

  const inner = (
    <>
      {/* Móvel: nunca fecha o wordmark (só o header desktop vira pílula). */}
      <Par marca={marca} tone={tone} fechado={false} hS={hM} hW={hMW} mostrarWordmark={mostrarWordmark} className="flex max-h-[28px] lg:hidden" />
      <Par marca={marca} tone={tone} fechado={fechado} hS={hS} hW={hW} mostrarWordmark={mostrarWordmark} className="hidden lg:flex" />
    </>
  )

  const cls = cn('group flex items-center focus:outline-none', className)
  if (!href) {
    return <span className={cls}>{inner}</span>
  }
  return (
    <LocaleLink href={href} className={cls} aria-label={`${marca.nome} — Início`}>
      {inner}
    </LocaleLink>
  )
}
